// src/hooks/useBeingsFilter.ts
import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PaginationParams } from '../services/api';

export type BeingType = 'clients' | 'pets' | 'clientPets';

export interface FilterState {
  letterFilter?: string;
  searchText?: string;
  hasPets?: boolean;
  hasBookings?: boolean;
  hasOverdueInvoices?: boolean;
  hasOutstandingBalance?: boolean;
  stillOwned?: boolean;
  petTypeIds?: number[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface UseBeingsFilterReturn {
  filterState: FilterState;
  setFilter: (key: keyof FilterState, value: any) => void;
  commitFilters: (state: FilterState) => void;
  clearFilters: () => void;
  applyFilters: () => void;
  activeFilterCount: number;
  isFilterActive: boolean;
  getPaginationParams: () => PaginationParams;
}

export const useBeingsFilter = (
  beingType: BeingType
): UseBeingsFilterReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filterState, setFilterState] = useState<FilterState>(() => ({
    letterFilter: searchParams.get('letterFilter') || undefined,
    searchText: searchParams.get('searchText') || undefined,
    hasPets:
      searchParams.get('hasPets') === 'true'
        ? true
        : searchParams.get('hasPets') === 'false'
          ? false
          : undefined,
    hasBookings:
      searchParams.get('hasBookings') === 'true'
        ? true
        : searchParams.get('hasBookings') === 'false'
          ? false
          : undefined,
    hasOverdueInvoices:
      searchParams.get('hasOverdueInvoices') === 'true'
        ? true
        : searchParams.get('hasOverdueInvoices') === 'false'
          ? false
          : undefined,
    hasOutstandingBalance:
      searchParams.get('hasOutstandingBalance') === 'true'
        ? true
        : searchParams.get('hasOutstandingBalance') === 'false'
          ? false
          : undefined,
    stillOwned:
      searchParams.get('stillOwned') === 'true'
        ? true
        : searchParams.get('stillOwned') === 'false'
          ? false
          : undefined,
    petTypeIds: searchParams
      .get('petTypeIds')
      ?.split(',')
      .map(Number)
      .filter((n) => n > 0),
    sortBy: searchParams.get('sortBy') || undefined,
    sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'asc',
  }));

  const setFilter = useCallback((key: keyof FilterState, value: any) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Commit a full filter state snapshot and sync to URL in one operation.
  // Used by the drawer on Apply to avoid multiple re-renders.
  const commitFilters = useCallback(
    (state: FilterState) => {
      setFilterState(state);
      const newParams = new URLSearchParams();
      Object.entries(state).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          newParams.set(
            key,
            Array.isArray(value) ? value.join(',') : String(value)
          );
        }
      });
      setSearchParams(newParams);
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    const clearedState: FilterState = { sortOrder: 'asc' };
    setFilterState(clearedState);
    const newParams = new URLSearchParams(searchParams);
    [
      'letterFilter',
      'searchText',
      'hasPets',
      'hasBookings',
      'hasOverdueInvoices',
      'hasOutstandingBalance',
      'stillOwned',
      'petTypeIds',
      'sortBy',
      'sortOrder',
    ].forEach((k) => newParams.delete(k));
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const applyFilters = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(filterState).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        newParams.set(
          key,
          Array.isArray(value) ? value.join(',') : String(value)
        );
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }, [filterState, searchParams, setSearchParams]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterState.letterFilter) count++;
    if (filterState.searchText) count++;
    if (filterState.hasPets !== undefined) count++;
    if (filterState.hasBookings !== undefined) count++;
    if (filterState.hasOverdueInvoices !== undefined) count++;
    if (filterState.hasOutstandingBalance !== undefined) count++;
    if (filterState.stillOwned !== undefined) count++;
    if (filterState.petTypeIds?.length) count++;
    return count;
  }, [filterState]);

  const isFilterActive = activeFilterCount > 0;

  const getPaginationParams = useCallback((): PaginationParams => {
    const params: PaginationParams = {};
    if (filterState.letterFilter)
      params.letterFilter = filterState.letterFilter;
    if (filterState.searchText) params.searchText = filterState.searchText;
    if (filterState.hasPets !== undefined) params.hasPets = filterState.hasPets;
    if (filterState.hasBookings !== undefined)
      params.hasBookings = filterState.hasBookings;
    if (filterState.hasOverdueInvoices !== undefined)
      params.hasOverdueInvoices = filterState.hasOverdueInvoices;
    if (filterState.hasOutstandingBalance !== undefined)
      params.hasOutstandingBalance = filterState.hasOutstandingBalance;
    if (filterState.stillOwned !== undefined)
      params.stillOwned = filterState.stillOwned;
    if (filterState.petTypeIds?.length)
      params.petTypeIds = filterState.petTypeIds;
    if (filterState.sortBy) params.sortBy = filterState.sortBy;
    if (filterState.sortOrder) params.sortOrder = filterState.sortOrder;
    return params;
  }, [filterState]);

  return {
    filterState,
    setFilter,
    commitFilters,
    clearFilters,
    applyFilters,
    activeFilterCount,
    isFilterActive,
    getPaginationParams,
  };
};
